import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';
import { loginRequest } from '../auth/auth-config';
import { loadSidekick, setSidekickVisible } from '../core/assistant-loader';
import { SidekickFocusService } from '../core/sidekick-focus.service';

/**
 * Mounts the runtime-loaded BT Sidekick assistant element for support staff.
 *
 * Renders no markup of its own -- `loadSidekick` injects `<bt-sidekick>` (a
 * floating launcher, `showLauncher: true`) directly into `document.body`, the
 * same app-wide-mount shape tms uses for its runtime-delivered widget. Placed
 * in the shell's authed branch only (see `app.component.ts`'s `bare()` gate),
 * so it never mounts on `/intake` or `/callback`.
 *
 * Page actions are OFF for support-staff: `core/assistant-loader.ts` has no
 * `actions` option at all for this host, so one cannot be wired in by accident.
 */
@Component({
  selector: 'app-sidekick-panel',
  standalone: true,
  template: '',
})
export class SidekickPanelComponent implements OnInit, OnDestroy {
  private msal = inject(MsalService);
  private focus = inject(SidekickFocusService);
  // Per-INSTANCE guard only -- prevents this instance from double-invoking
  // mount() re-entrantly. It is NOT what keeps <bt-sidekick> mounted exactly
  // once across a bare()<->non-bare() flip that recreates this component
  // (a fresh instance gets a fresh `loaded=false`); that guarantee lives at
  // module scope in `core/assistant-loader.ts` (F1 review, fix round 1).
  private loaded = false;
  // Set in ngOnDestroy so an in-flight mount() that resolves AFTER a bare-route
  // flip does not re-show the widget over /intake or /callback (codex-review,
  // 2026-08-11).
  private destroyed = false;

  /**
   * The in-flight/settled mount, exposed so a spec can `await` the real async
   * chain deterministically instead of racing `ComponentFixture.whenStable()`
   * (which does not reliably track a spied `fetch`'s promise chain).
   */
  mounted: Promise<void> = Promise.resolve();

  ngOnInit(): void {
    this.mounted = this.mount();
  }

  private async mount(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    const a = environment.assistant;
    try {
      await loadSidekick({
        // Both point at bluetitan-api -- the assistant's host, NOT
        // environment.apiBaseUrl (the bluetitan-support ticket service).
        brainBaseUrl: a.brainBaseUrl,
        apiBaseUrl: a.brainBaseUrl,
        channel: a.bundleChannel,
        target: document.body,
        surface: 'support-staff',
        showLauncher: true,
        embedComposer: true,
        getAccessToken: () => this.getAccessToken(),
        getPageContext: () => ({ ticket_id: this.focus.getFocusedTicketId() }),
        userInitials: this.userInitials(),
      });
      // Re-show the app-wide widget: this instance is live on an authed route.
      // On a bare()<->authed flip the element persists (mount-once), so a
      // recreated instance re-shows the SAME element that ngOnDestroy hid.
      // Skip if this instance was destroyed while loadSidekick was in flight —
      // otherwise a late resolve would re-show the widget over a bare route.
      if (!this.destroyed) setSidekickVisible(true);
    } catch {
      // Allow THIS instance to retry if re-triggered. The real cross-instance
      // retry-after-failure guarantee is `loadSidekick`'s own guard reset --
      // see assistant-loader.ts.
      this.loaded = false;
    }
  }

  ngOnDestroy(): void {
    // The component lives in the authed shell branch only; app.component destroys
    // it when bare() flips to /intake or /callback. The <bt-sidekick> element in
    // document.body outlives that (mount-once), so hide it here so it never shows
    // over a bare route (codex-review, 2026-08-11).
    this.destroyed = true;
    setSidekickVisible(false);
  }

  /**
   * Reuse the console's EXISTING MSAL token path -- the same
   * `acquireTokenSilent({ ...loginRequest, account })` call `auth.interceptor.ts`
   * makes for every support-API request (same scopes, same account resolution).
   * Deliberately not a second auth path. See task-F1-report.md for the audience
   * caveat this defers: the resulting token is scoped for THIS console's own
   * `access_as_agent` app registration, not necessarily one bluetitan-api accepts.
   */
  /**
   * The signed-in agent's initials (e.g. "KE") for the sidekick's user-message
   * avatar. Mirrors the console profile chip's derivation exactly (AppComponent
   * `initials`): local part of the email, split on ./-/_, first char of the first
   * two parts, else the first two chars. Returns undefined when no account/email,
   * so the widget falls back to its neutral avatar.
   */
  private userInitials(): string | undefined {
    const account = this.msal.instance.getActiveAccount();
    const email = account?.username ?? '';
    const name = email.split('@')[0] ?? '';
    const parts = name.split(/[.\-_]+/).filter(Boolean);
    const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
    const out = (chars || '').toUpperCase();
    return out || undefined;
  }

  private async getAccessToken(): Promise<string | null> {
    const account = this.msal.instance.getActiveAccount();
    if (!account) return null;
    try {
      const result = await firstValueFrom(this.msal.acquireTokenSilent({ ...loginRequest, account }));
      return result.accessToken;
    } catch {
      return null;
    }
  }
}
